import type {
  AccountNeedsResizeMap,
  AccountResizeStatusMap,
  DesignSelection,
  PosterNeedsResizeInformation,
  SocialMediaResizeAjaxParams,
  SocialMediaResizeResponse,
} from '@Components/social-media/social-media.types';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {PosterResizeRequiredStatus} from '@Components/social-media/social-media.types';
import type {SocialMediaAccountId} from '@Components/social-media/post.vo';
import {asyncnoop} from '@/utils/general.util';

// Matches the backend publish-time wait (SocialMediaFacade::WAIT_TIME_FOR_GRAPHIC_GENERATION = 600s).
export const VIDEO_RESIZE_POLLING_TIMEOUT = 600000;
export const PORTRAIT_ASPECT_RATIO = 9 / 16;
export const SQUARE_ASPECT_RATIO = 1;

export const doesVideoExist = (designPagesVideo: boolean[]): boolean => {
  return designPagesVideo.includes(true);
};

export const areAllDesignPagesSupportedForAccounts = (accountNeedsResizeInformation: AccountNeedsResizeMap, socialAccountIds: SocialMediaAccountId[]): boolean => {
  for (const accountId of socialAccountIds) {
    if (isSizeNotSupportedForAccountId(accountId, accountNeedsResizeInformation)) {
      return false;
    }
  }

  return true;
};

const isSizeNotSupportedForAccountId = (accountId: SocialMediaAccountId, accountResizingNeededMap: AccountNeedsResizeMap): boolean => {
  const allPageResizeInfo = getAllPageResizeInfoForAccount(accountResizingNeededMap, accountId);

  return allPageResizeInfo.some(
    (pageResizeInfo) => pageResizeInfo.needsResizing === PosterResizeRequiredStatus.NOT_SUPPORTED || pageResizeInfo.reelNeedsResizing === PosterResizeRequiredStatus.NOT_SUPPORTED
  );
};

export const getAllPageResizeInfoForAccount = (accountNeedsResizeMapForDesigns: AccountNeedsResizeMap, accountId: SocialMediaAccountId): PosterNeedsResizeInformation[] => {
  if (!accountNeedsResizeMapForDesigns[accountId]) {
    return [];
  }

  return Object.values(accountNeedsResizeMapForDesigns[accountId]).flatMap((pageResizeInfoMap) => Object.values(pageResizeInfoMap));
};

export const getAllDesignResizeStatuses = (accountResizeStatusMap: AccountResizeStatusMap): AccountResizeStatus[] => {
  return Object.values(accountResizeStatusMap).flatMap((pageResizeStatusMap) => Object.values(pageResizeStatusMap));
};

export const resolveAccountResizeStatus = (statusesForDesigns: AccountResizeStatus[]): AccountResizeStatus => {
  if (statusesForDesigns.includes(AccountResizeStatus.RESIZE_ERROR)) {
    return AccountResizeStatus.RESIZE_ERROR;
  }

  return statusesForDesigns.find((status) => status !== AccountResizeStatus.RESIZE_DONE) ?? AccountResizeStatus.RESIZE_DONE;
};

export const toDesignSelection = ({posterHashedId, pageHashedId}: SocialMediaResizeAjaxParams): DesignSelection => {
  return {posterHashedId, pageHashedId};
};

export const isSameDesign = (design: DesignSelection, otherDesign: DesignSelection): boolean => {
  return design.posterHashedId === otherDesign.posterHashedId && design.pageHashedId === otherDesign.pageHashedId;
};

/**
 * Resizes a design for a platform and returns its graphic URL. Throws (never returns '') when the render
 * times out or fails: an empty URL silently saved here is what later makes the backend reject the post with
 * "Invalid GraphicsMeta was provided for the selected design". Throwing routes the design to RESIZE_ERROR.
 */
export const makeSocialMediaResizeAjaxCall = async (ajaxResizeParams: SocialMediaResizeAjaxParams, isVideo: boolean | undefined): Promise<string> => {
  const resizeResponse: SocialMediaResizeResponse = (await window.PMW.writeLocal('socialmedia/getResizedDesign', ajaxResizeParams)) as SocialMediaResizeResponse;

  if (!resizeResponse) {
    throw new Error('Unable to resize');
  }

  const {s3FilePath} = resizeResponse;

  if (s3FilePath) {
    return s3FilePath;
  }

  if (!isVideo || !resizeResponse.videoS3FileName) {
    throw new Error('Unable to resize');
  }

  const pollingData = (await window.PMW.pollingAjaxCallAsync(
    asyncnoop,
    () => {
      return window.PMW.readLocal('socialmedia/checkVideoResizeStatus', {
        s3FileName: resizeResponse.videoS3FileName,
        posterHashId: ajaxResizeParams.posterHashedId,
        pageHashId: ajaxResizeParams.pageHashedId,
        idPost: ajaxResizeParams.idPost,
        socialAccountId: ajaxResizeParams.socialAccountId,
        platformMediaType: ajaxResizeParams.platformMediaType,
      });
    },
    VIDEO_RESIZE_POLLING_TIMEOUT
  )) as string;

  if (pollingData) {
    return pollingData;
  }

  throw new Error('Unable to resize');
};

/**
 * Generates a video preview and returns its URL. Like makeSocialMediaResizeAjaxCall, throws instead of
 * returning '' on timeout/failure so the design is marked RESIZE_ERROR rather than becoming publishable
 * with an empty graphic URL.
 */
export const makeVideoPreviewGenerationAjaxCall = async (design: DesignSelection): Promise<string> => {
  const response: SocialMediaResizeResponse = (await window.PMW.writeLocal('socialmedia/generateVideoPreview', design)) as SocialMediaResizeResponse;

  if (!response) {
    throw new Error('Unable to generate video preview');
  }

  const {s3FilePath} = response;

  if (s3FilePath) {
    return s3FilePath;
  }

  if (!response.videoS3FileName) {
    throw new Error('Unable to generate video preview');
  }

  const pollingData = (await window.PMW.pollingAjaxCallAsync(
    asyncnoop,
    () => {
      return window.PMW.readLocal('socialmedia/checkVideoPreviewStatus', {
        s3FileName: response.videoS3FileName,
        posterHashId: design.posterHashedId,
        pageHashId: design.pageHashedId,
      });
    },
    VIDEO_RESIZE_POLLING_TIMEOUT
  )) as string;

  if (pollingData) {
    return pollingData;
  }

  throw new Error('Unable to generate video preview');
};
